import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Annotation, ViewTransform, Point, BoundingBox, Tool, MouseButton } from '../types';

interface ImageCanvasProps {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  annotations: Annotation[];
  selectedAnnotation: number | null;
  currentTool: Tool;
  currentClass: number;
  onAnnotationCreate: (bbox: BoundingBox, classId: number) => void;
  onAnnotationSelect: (id: number | null) => void;
  onAnnotationDelete: (id: number) => void;
  onAnnotationEdit?: (id: number) => void;
  onAnnotationUpdate?: (id: number, updates: Partial<Annotation>) => void;
  className?: string;
  transform: ViewTransform;
  onTransformChange: React.Dispatch<React.SetStateAction<ViewTransform>>;
}

const CLASS_COLORS = {
  0: '#ef4444', // red - straight
  1: '#3b82f6', // blue - L-shape
  2: '#10b981', // green - U-shape
  3: '#f59e0b'  // orange - complex
};

const CLASS_NAMES = {
  0: 'straight',
  1: 'L-shape',
  2: 'U-shape',
  3: 'complex'
};

export const ImageCanvas = React.forwardRef<HTMLCanvasElement, ImageCanvasProps>(({
  imageUrl,
  imageWidth,
  imageHeight,
  annotations,
  selectedAnnotation,
  currentTool,
  currentClass,
  onAnnotationCreate,
  onAnnotationSelect,
  onAnnotationDelete,
  onAnnotationEdit,
  onAnnotationUpdate,
  className = '',
  transform,
  onTransformChange
}, ref) => {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = (ref as React.MutableRefObject<HTMLCanvasElement | null>) || internalCanvasRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const setTransform = onTransformChange;
  const [isDragging, setIsDragging] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [currentBBox, setCurrentBBox] = useState<BoundingBox | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // מצב גרירת אנוטציה
  const [isDraggingAnnotation, setIsDraggingAnnotation] = useState(false);
  const [draggedAnnotationId, setDraggedAnnotationId] = useState<number | null>(null);
  const [annotationDragStart, setAnnotationDragStart] = useState<Point | null>(null);
  const [isHoveringSelectedAnnotation, setIsHoveringSelectedAnnotation] = useState(false);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      fitImageToCanvas();
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Fit image to canvas on window resize
  useEffect(() => {
    const handleResize = () => {
      if (imageLoaded) {
        fitImageToCanvas();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded]);

  const fitImageToCanvas = useCallback(() => {
    if (!containerRef.current || !imageLoaded) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond original size

    const scaledWidth = imageWidth * scale;
    const scaledHeight = imageHeight * scale;

    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    setTransform({ x, y, scale });
  }, [imageWidth, imageHeight, imageLoaded]);

  // Convert screen coordinates to image coordinates
  const screenToImage = useCallback((screenX: number, screenY: number): Point => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = screenX - rect.left;
    const canvasY = screenY - rect.top;

    const imageX = (canvasX - transform.x) / transform.scale;
    const imageY = (canvasY - transform.y) / transform.scale;

    return { x: imageX, y: imageY };
  }, [transform]);



  // Handle mouse events
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    const button = event.button as MouseButton;
    const point = screenToImage(event.clientX, event.clientY);

    // Pan with middle button, Shift+left click, or Pan tool
    if (button === MouseButton.MIDDLE ||
        (button === MouseButton.LEFT && event.shiftKey) ||
        currentTool === Tool.PAN) {
      // Pan mode
      setIsPanning(true);
      setDragStart({ x: event.clientX, y: event.clientY });
      event.preventDefault();
      return;
    }

    if (button === MouseButton.LEFT && currentTool === Tool.BBOX && !event.shiftKey) {
      // בדוק אם לוחצים על אנוטציה קיימת
      const clickedAnnotation = annotations.find(ann =>
        point.x >= ann.x1 && point.x <= ann.x2 &&
        point.y >= ann.y1 && point.y <= ann.y2
      );

      if (clickedAnnotation) {
        // אם לוחצים על אנוטציה שכבר נבחרה - התחל גרירה
        if (clickedAnnotation.id === selectedAnnotation && onAnnotationUpdate) {
          setIsDraggingAnnotation(true);
          setDraggedAnnotationId(clickedAnnotation.id);
          setAnnotationDragStart(point);
          event.preventDefault();
        } else {
          // אחרת - פשוט בחר אותה
          onAnnotationSelect(clickedAnnotation.id);
        }
      } else {
        // התחל ציור bounding box חדש
        setIsDragging(true);
        setDragStart(point);
        setCurrentBBox({
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y
        });
        onAnnotationSelect(null);
      }
    }
  }, [currentTool, annotations, onAnnotationSelect, screenToImage, selectedAnnotation, onAnnotationUpdate]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    const point = screenToImage(event.clientX, event.clientY);

    // בדוק אם מרחפים מעל אנוטציה נבחרת
    if (!isPanning && !isDragging && !isDraggingAnnotation && currentTool === Tool.BBOX) {
      const hoveredAnnotation = annotations.find(ann =>
        point.x >= ann.x1 && point.x <= ann.x2 &&
        point.y >= ann.y1 && point.y <= ann.y2
      );
      setIsHoveringSelectedAnnotation(
        hoveredAnnotation !== undefined && hoveredAnnotation.id === selectedAnnotation
      );
    }

    if (isPanning && dragStart) {
      // Pan the image
      const deltaX = event.clientX - dragStart.x;
      const deltaY = event.clientY - dragStart.y;

      setTransform(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));

      setDragStart({ x: event.clientX, y: event.clientY });
    } else if (isDraggingAnnotation && annotationDragStart && draggedAnnotationId !== null && onAnnotationUpdate) {
      // גרירת אנוטציה - עדכן מיקום בזמן אמת
      const dx = point.x - annotationDragStart.x;
      const dy = point.y - annotationDragStart.y;

      const annotation = annotations.find(ann => ann.id === draggedAnnotationId);
      if (annotation) {
        const newX1 = annotation.x1 + dx;
        const newY1 = annotation.y1 + dy;
        const newX2 = annotation.x2 + dx;
        const newY2 = annotation.y2 + dy;

        // עדכן את מיקום האנוטציה
        onAnnotationUpdate(draggedAnnotationId, {
          x1: Math.round(newX1),
          y1: Math.round(newY1),
          x2: Math.round(newX2),
          y2: Math.round(newY2),
          width: Math.round(newX2 - newX1),
          height: Math.round(newY2 - newY1)
        });

        // עדכן את נקודת ההתחלה למשיכה החלקה
        setAnnotationDragStart(point);
      }
    } else if (isDragging && dragStart && currentBBox) {
      // Update current bounding box
      setCurrentBBox({
        x1: Math.min(dragStart.x, point.x),
        y1: Math.min(dragStart.y, point.y),
        x2: Math.max(dragStart.x, point.x),
        y2: Math.max(dragStart.y, point.y)
      });
    }
  }, [isPanning, isDragging, isDraggingAnnotation, dragStart, annotationDragStart, draggedAnnotationId, screenToImage, setTransform, onAnnotationUpdate, annotations, currentTool, selectedAnnotation]);

  const handleMouseUp = useCallback(() => {
    if (isDragging && currentBBox) {
      // Create new annotation if bbox is large enough
      const minSize = 10 / transform.scale; // Minimum 10 pixels at current zoom
      if (Math.abs(currentBBox.x2 - currentBBox.x1) > minSize &&
          Math.abs(currentBBox.y2 - currentBBox.y1) > minSize) {
        onAnnotationCreate(currentBBox, currentClass);
      }
    }

    // סיים גרירת אנוטציה
    if (isDraggingAnnotation) {
      setIsDraggingAnnotation(false);
      setDraggedAnnotationId(null);
      setAnnotationDragStart(null);
    }

    setIsDragging(false);
    setIsPanning(false);
    setDragStart(null);
    setCurrentBBox(null);
  }, [isDragging, isDraggingAnnotation, currentBBox, transform.scale, onAnnotationCreate, currentClass]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    if (selectedAnnotation !== null) {
      onAnnotationDelete(selectedAnnotation);
    }
  }, [selectedAnnotation, onAnnotationDelete]);

  const handleDoubleClick = useCallback((event: React.MouseEvent) => {
    if (onAnnotationEdit && currentTool === Tool.POINTER) {
      const point = screenToImage(event.clientX, event.clientY);

      // מצא את התיוג שנלחץ
      const clickedAnnotation = annotations.find(ann =>
        point.x >= ann.x1 && point.x <= ann.x2 &&
        point.y >= ann.y1 && point.y <= ann.y2
      );

      if (clickedAnnotation) {
        onAnnotationEdit(clickedAnnotation.id);
      }
    }
  }, [currentTool, annotations, onAnnotationEdit, screenToImage]);

  // Draw everything on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded || !imageRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context
    ctx.save();

    // Apply transform
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.scale, transform.scale);

    // Draw image
    ctx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);

    // Draw annotations
    annotations.forEach(annotation => {
      const color = CLASS_COLORS[annotation.class_id as keyof typeof CLASS_COLORS] || '#666';
      const isSelected = annotation.id === selectedAnnotation;

      ctx.strokeStyle = color;
      ctx.lineWidth = isSelected ? 3 / transform.scale : 2 / transform.scale;
      ctx.setLineDash([]);

      // Draw bounding box
      ctx.strokeRect(
        annotation.x1,
        annotation.y1,
        annotation.width,
        annotation.height
      );

      // Draw class label
      const className = CLASS_NAMES[annotation.class_id as keyof typeof CLASS_NAMES] || 'unknown';
      const label = `${annotation.class_id}: ${className}`;

      ctx.fillStyle = color;
      const fontSize = 12 / transform.scale;
      ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;

      const textMetrics = ctx.measureText(label);
      const textWidth = textMetrics.width;
      const textHeight = fontSize;

      // Label background
      ctx.fillRect(
        annotation.x1,
        annotation.y1 - textHeight - 4,
        textWidth + 8,
        textHeight + 4
      );

      // Label text
      ctx.fillStyle = 'white';
      ctx.fillText(label, annotation.x1 + 4, annotation.y1 - 4);
    });

    // Draw current bounding box being drawn
    if (currentBBox && isDragging) {
      const color = CLASS_COLORS[currentClass as keyof typeof CLASS_COLORS] || '#666';
      ctx.strokeStyle = color;
      ctx.lineWidth = 2 / transform.scale;
      ctx.setLineDash([5 / transform.scale, 5 / transform.scale]);

      ctx.strokeRect(
        currentBBox.x1,
        currentBBox.y1,
        currentBBox.x2 - currentBBox.x1,
        currentBBox.y2 - currentBBox.y1
      );
    }

    // Restore context
    ctx.restore();
  }, [transform, annotations, selectedAnnotation, currentBBox, isDragging, currentClass, imageLoaded, imageWidth, imageHeight]);

  // Update canvas size only when container dimensions change
  useEffect(() => {
    if (canvasRef.current && containerRef.current) {
      const canvas = canvasRef.current;
      const container = containerRef.current;

      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      // Only update if dimensions actually changed (to avoid clearing canvas unnecessarily)
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth;
        canvas.height = newHeight;
      }
    }
  }, []); // Run once on mount

  // Handle window resize to update canvas size
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && containerRef.current) {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const newWidth = container.clientWidth;
        const newHeight = container.clientHeight;

        if (canvas.width !== newWidth || canvas.height !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add native wheel event with passive: false for better control
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleNativeWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Detect pinch gesture
      const isPinch = e.ctrlKey || e.metaKey;

      if (isPinch && e.deltaY !== 0) {
        // Pinch-to-zoom with lower sensitivity for smoother experience
        const ZOOM_SENSITIVITY = 0.02;
        const scaleFactor = e.deltaY > 0 ? 1 - ZOOM_SENSITIVITY : 1 + ZOOM_SENSITIVITY;
        const currentScale = transform.scale;
        const newScale = Math.max(0.1, Math.min(5, currentScale * scaleFactor));
        const scaleRatio = newScale / currentScale;

        // Zoom towards mouse position
        setTransform(prev => {
          const newX = mouseX - (mouseX - prev.x) * scaleRatio;
          const newY = mouseY - (mouseY - prev.y) * scaleRatio;
          return { x: newX, y: newY, scale: newScale };
        });
      } else {
        // Pan with trackpad - lower sensitivity for smoother experience
        const PAN_SENSITIVITY = 0.5;
        setTransform(prev => ({
          ...prev,
          x: prev.x - e.deltaX * PAN_SENSITIVITY,
          y: prev.y - e.deltaY * PAN_SENSITIVITY
        }));
      }
    };

    canvas.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleNativeWheel);
  }, [transform.scale, setTransform]);

  const getCursorStyle = () => {
    if (isPanning) return 'grabbing';
    if (isDraggingAnnotation) return 'move';
    if (isHoveringSelectedAnnotation && currentTool === Tool.BBOX) return 'move';
    if (currentTool === Tool.PAN) return 'grab';
    if (currentTool === Tool.BBOX) return 'crosshair';
    return 'default';
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{ cursor: getCursorStyle() }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
});